import { animate, state, style, transition, trigger } from "@angular/animations";
import { SelectionModel } from "@angular/cdk/collections";
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Select, Store } from "@ngxs/store";
import { PermissionsService } from "@zonar-ui/auth";
import { IDivision } from "@zonar-ui/auth/lib/models/company.model";
import startOfDay from "date-fns/startOfDay";
import subDays from "date-fns/subDays";
import { combineLatest, Observable, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, take } from "rxjs/operators";
import { PaginatorComponent } from "src/app/components/paginator/paginator.component";
import { translateAndFormat } from "src/app/i18next";
import { formatDate } from "src/app/i18next/formatDate";
import { Defect, DefectMedia } from "src/app/models/defect.model";
import { FilterCardQueryStrings, QueryStrings } from "src/app/models/emitter-events.models";
import { ExportType } from "src/app/models/exportType";
import { LanguageDictionary } from "src/app/models/language-dictionary.model";
import { AggregateModalData } from "src/app/models/modal-data.models";
import { ExportService } from "src/app/services/export/export.service";
import { LanguageDictionaryService } from "src/app/services/language-dictionary/language-dictionary.service";
import { MemCacheService } from "src/app/services/mem-cache/mem-cache.service";
import { environment } from "src/environments/environment";
import { getViewInspectionsRoleUUID } from "src/utils/getViewInspectionsRoleUUID/getViewInspectionsRoleUUID";
import { isDefined } from "src/utils/isDefined/isDefined";
import { newDate } from "src/utils/newDate/newDate";
import { AppState, SetSearchTable } from "../../app.state";
import { EnabledFilters } from "../../components/filter-card/filter-card.component";
import { InspectionType } from "../../models/inspection-type.models";
import { ClosedDefectTableViewModel, OpenDefectTableViewModel } from "../../models/open-defect-table.models";
import { PhotoViewModelService } from "../../services/photo-view-model.service";
import { SeverityIconService } from "../../services/severity-icon.service";
import { ImageCarouselDialogComponent } from "./../../components/image-carousel-dialog/image-carousel-dialog.component";
import { DefectListState, GetDefectListWithQuery } from "./state/defect-list.state";

export interface DefectsSortState {
	sortActive: string;
	sortDirection: string;
}

export enum DefectStatus {
	OPEN = "open",
	CLOSED = "closed",
}

const stateKey = "defects";

@Component({
	selector: "app-defect-list",
	templateUrl: "./defect-list.component.html",
	styleUrls: ["./defect-list.component.scss"],
	encapsulation: ViewEncapsulation.None,
	animations: [
		// expandable rows example: https://material.angular.io/components/table/examples
		// added void: https://github.com/angular/components/issues/11990
		trigger("detailExpand", [
			state("collapsed, void", style({ height: "0px", minHeight: "0" })),
			state("expanded", style({ height: "*" })),
			transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
			transition("expanded <=> void", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
		]),
		trigger("rotatedState", [
			state("down", style({ transform: "rotate(0)" })),
			state("up", style({ transform: "rotate(180deg)" })),
			transition("down <=> up", animate("225ms cubic-bezier(0.4,0.0,0.2,1)")),
		]),
	],
})
export class DefectListComponent implements OnInit, OnDestroy {
	sortActive = "last-inspected-date";
	sortDirection = "desc";
	state: DefectsSortState;
	@ViewChild("appPaginator") paginatorComponent!: PaginatorComponent;

	// Store Selects
	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;
	@Select(AppState.selectLanguageDictionary) languageDictionary$: Observable<LanguageDictionary>;
	@Select(AppState.selectUserDivisions) userDivisions$: Observable<Array<IDivision>>;
	@Select(AppState.selectUserHasAllDivisions) userHasAllDivisions$: Observable<boolean>;
	@Select(AppState.selectUserRoles) selectUserRoles$: Observable<Array<string>>;
	@Select(DefectListState.getDefectList) defects$: Observable<ReturnType<typeof DefectListState.getDefectList>>;
	@Select(DefectListState.getTotalCount) totalDefects$: Observable<{
		totalCount: string;
		companyId: string;
	}>;

	public config = {
		initialSelection: [],
		allowMultiSelect: true,
	};
	public canAssignMechanic = false;
	public companyId = "";
	public dataSource: MatTableDataSource<OpenDefectTableViewModel | ClosedDefectTableViewModel> =
		new MatTableDataSource();
	public defects: Defect[] = [];
	public defectSubscription: Subscription;
	public disabledRepairButtons: boolean;
	public expandedElement: OpenDefectTableViewModel | ClosedDefectTableViewModel | null;
	public headerDate = "date inspected";
	public isInitiallyLoading = true;
	public modifySearchString: string = translateAndFormat("please modify your search and try again", "capitalize");
	public pageIndex = 0;
	public pageSize = 10;
	public pageSizeOptions = [10, 20, 50, 100, 200];
	public permissions = false;
	public repairEnabled = true;
	public rotateState: string[] = [];
	public userHasAllDivisions = false;
	public userDivisions: ReadonlyArray<IDivision>;
	private searchTables = [
		{
			tableName: "assets",
			tableQueryName: "assets",
			displayTableName: translateAndFormat("assets", "capitalize"),
			displayFieldNames: ["assetName"],
			fieldNames: ["assetName"],
		},
		{
			tableName: "inspectors",
			tableQueryName: "inspectors",
			displayTableName: translateAndFormat("inspectors", "capitalize"),
			displayFieldNames: ["inspectorFirstName", "inspectorLastName"],
			fieldNames: ["inspectorFirstName", "inspectorLastName"],
		},
		{
			tableName: "divisions",
			tableQueryName: "divisions",
			displayTableName: translateAndFormat("divisions", "capitalize"),
			displayFieldNames: ["divisionName"],
			fieldNames: ["divisionName"],
		},
	] as const;
	public selectionModel = new SelectionModel(this.config.allowMultiSelect, this.config.initialSelection);
	public statusFilterValue: DefectStatus = DefectStatus.OPEN;
	public totalDefects = 0;
	public totalDefectSubscription: Subscription;

	// set the initial defaults for filters, sort, and pagination
	public currentQueryStrings: QueryStrings = {
		page: "1",
		perPage: this.pageSize.toString(),
		sort: "lastInspected",
		sortDir: "desc",
		dates: [{ startTime: startOfDay(subDays(newDate(), 7)), endTime: newDate() }],
	};
	public displayedColumns: string[] = [
		"select",
		"severity",
		"last-inspected-date",
		"last-inspected-time",
		"reconciled-asset-name",
		"location",
		"zone-label",
		"component-label",
		"condition-label",
		"chevron",
	];
	public enabledFilters: EnabledFilters = {
		assetId: false,
		assetType: false,
		customDateRange: false,
		dateRange: false,
		defectType: false,
		inspectionType: false,
		homeLocation: false,
		inspector: false,
		page: "",
		severity: false,
		tableFilter: false,
		resetFilterButton: false,
		searchBar: false,
		inspectionStatus: false,
	};
	sortQueryStringValues = () => ({
		severity: "severity",
		"last-inspected-date":
			JSON.stringify(this.currentQueryStrings.statuses) === JSON.stringify(["open", "pending"])
				? "lastInspected"
				: "repairDate",
		"reconciled-asset-name": "assetName",
		"zone-label": "zone",
		"component-label": "component",
		"condition-label": "condition",
	});

	onDestroy$ = new Subject<void>();

	constructor(
		private exportService: ExportService,
		private languageDictionaryService: LanguageDictionaryService,
		private memCacheService: MemCacheService,
		private permissionsService: PermissionsService,
		private photoViewModelService: PhotoViewModelService,
		private severityIconService: SeverityIconService,
		private store: Store,
		public dialog: MatDialog,
	) {}

	ngOnInit() {
		this.memCacheService.setValue("isLoadingPage", true);

		// Set filter views
		this.enabledFilters = {
			assetId: false,
			assetType: true,
			customDateRange: false,
			dateRange: true,
			defectType: true,
			inspectionType: false,
			homeLocation: false,
			inspector: false,
			page: "defects",
			severity: true,
			tableFilter: true,
			resetFilterButton: false,
			searchBar: true,
			inspectionStatus: false,
		};

		this.selectUserRoles$.pipe(filter(Boolean), take(1)).subscribe((selectUserRoles: Array<string>) => {
			this.disabledRepairButtons =
				selectUserRoles.length === 1 && selectUserRoles[0] === getViewInspectionsRoleUUID();
		});

		combineLatest([this.selectedCompanyId$, this.userDivisions$, this.userHasAllDivisions$])
			.pipe(
				filter(
					([selectedCompanyId, userDivisions, userHasAllDivisions]) =>
						isDefined(selectedCompanyId) && isDefined(userDivisions) && isDefined(userHasAllDivisions),
				),
			)
			.subscribe(([selectedCompanyId, userDivisions, userHasAllDivisions]) => {
				this.memCacheService.clear(stateKey);
				this.companyId = selectedCompanyId;
				this.userDivisions = userDivisions;
				this.userHasAllDivisions = userHasAllDivisions;
				this.updateTable(true);

				this.permissionsService
					.getPermissions()
					.subscribe(
						(permissions: {
							evir?: { mechanic?: { assign?: unknown } };
							_unrestrictedApps?: ReadonlySet<string>;
						}) => {
							this.permissions = true;
							this.canAssignMechanic =
								permissions?.evir?.mechanic?.assign !== undefined ||
								permissions?._unrestrictedApps?.has(
									environment.environmentConstants.APP_APPLICATION_ID,
								);
						},
					);
			});

		this.languageDictionary$.pipe(filter(isDefined)).subscribe(() => {
			this.defectSubscription = this.defects$
				.pipe(
					filter(
						defectData =>
							isDefined(defectData) &&
							Object.values(defectData).every(isDefined) &&
							defectData.companyId === this.companyId,
					),
					// only emit if the previous array of defects differs from latest array: https://www.learnrxjs.io/operators/filtering/distinctuntilchanged.html
					// solved issue of subscription firing twice due to data already in state
					distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
				)
				.subscribe(({ defects }) => {
					const openDefects = this.statusFilterValue === DefectStatus.OPEN;

					this.headerDate = openDefects ? "date inspected" : "date closed";

					const defectView = openDefects
						? this.buildDefectListViewModel(defects)
						: this.buildClosedDefectListViewModel(defects);

					this.dataSource = new MatTableDataSource<OpenDefectTableViewModel | ClosedDefectTableViewModel>(
						defectView,
					);
					this.defects = defects;

					// initialize all chevrons in view to down since no expansion should happen on init
					this.rotateState = new Array(this.pageSize).fill("down");

					this.isInitiallyLoading = false;
				});
		});

		// get the total number of defects to pass into the paginator
		this.totalDefectSubscription = this.totalDefects$
			.pipe(filter(defects => isDefined(defects.totalCount) && defects.companyId === this.companyId))
			.subscribe(({ totalCount }) => {
				this.totalDefects = parseInt(totalCount, 10);
			});

		this.memCacheService.cacheChanged$(stateKey).subscribe((value: DefectsSortState) => {
			this.state = value;
		});

		this.initState();
	}

	initState() {
		this.state = this.getState() ?? {
			sortActive: this.sortActive,
			sortDirection: this.sortDirection,
		};

		this.sortActive = this.state.sortActive;
		this.sortDirection = this.state.sortDirection;

		this.store.dispatch(new SetSearchTable(this.searchTables));
	}

	flushState() {
		this.memCacheService.setValue<DefectsSortState>(stateKey, {
			sortActive: this.sortActive,
			sortDirection: this.sortDirection,
		});
	}

	getState() {
		return this.memCacheService.getValue<DefectsSortState>(stateKey);
	}

	getSeverityIcon(severity: number): string {
		return this.severityIconService.getSeverityIcon(severity);
	}

	// For checkbox aria-labels
	checkboxLabel(row: OpenDefectTableViewModel): string {
		if (!row) {
			// master checkbox
			return `${this.isAllSelected() ? "deselect" : "select"} all`;
		} else {
			// Single checkbox
			return `${this.selectionModel.isSelected(row) ? "deselect" : "select"} row ${row.zoneLabel} ${
				row.componentLabel
			} ${row.conditionLabel}`;
		}
	}

	getDividerClass(row: OpenDefectTableViewModel, i: number): string {
		if (this.selectionModel.isSelected(row) && this.rotateState[i] === "down") {
			return "highlight";
		}

		if (this.selectionModel.isSelected(row) && this.rotateState[i] === "up") {
			return "white-divider-highlight";
		}
	}

	// when user clicks on chevron, show or hide hidden notes
	showExpansion(element: OpenDefectTableViewModel): void {
		this.expandedElement = this.expandedElement === element ? null : element;
	}

	// trigger slide in slide out animation and set class to hide or show
	expansionAnimation(element: OpenDefectTableViewModel, expandedElement: OpenDefectTableViewModel): string {
		return element === expandedElement ? "expanded" : "collapsed";
	}

	// rotate the chevron that corresponds with the index of clicked row
	// e.g. rotateState = ['down', 'down'], user clicks second row, rotateState now ['down', 'up']
	// triggers animation for clicked chevron
	rotate(i: number): void {
		this.rotateState = this.rotateState.map((rotation: string, k: number) => {
			if (k === i) {
				return rotation === "down" ? "up" : "down";
			} else {
				return "down";
			}
		});
	}

	// Handles backend sorting
	onMatSortChange(event: Sort): void {
		// if user clicked and arrow is up / ascending, even.direction = asc, else event.direction = desc
		this.currentQueryStrings = {
			...this.currentQueryStrings,
			sort: this.sortQueryStringValues()[event.active],
			sortDir: event.direction,
		};
		this.selectionModel.clear();
		this.updateTable(true);

		this.sortActive = event.active;
		this.sortDirection = event.direction;

		this.flushState();
	}

	// handle filter queries from the filter card component and update table
	handleFilterValues(filterQueries: FilterCardQueryStrings): void {
		const defectStatus = filterQueries.statuses;
		const currentFilterValue =
			defectStatus && JSON.stringify(defectStatus) !== JSON.stringify(["open", "pending"])
				? DefectStatus.CLOSED
				: DefectStatus.OPEN;
		this.statusFilterValue = currentFilterValue;
		this.repairEnabled = currentFilterValue === DefectStatus.OPEN;
		const { page, perPage, sort, sortDir } = this.currentQueryStrings;
		this.currentQueryStrings = {
			page,
			perPage,
			sort,
			sortDir,
			...filterQueries,
		};
		if (this.sortActive === "last-inspected-date") {
			this.currentQueryStrings = {
				...this.currentQueryStrings,
				sort: this.sortQueryStringValues()[this.sortActive],
			};
		}
		this.selectionModel.clear();

		this.updateTable(true);
	}

	// We get back an emitted page event and query string from the child paginator component
	onQueryChange(query: URLSearchParams): void {
		this.pageIndex = parseInt(query.get("page"));
		this.pageSize = parseInt(query.get("perPage"));

		this.currentQueryStrings = {
			...this.currentQueryStrings,
			page: this.pageIndex.toString(),
			perPage: this.pageSize.toString(),
		};

		this.selectionModel.clear();
		this.updateTable();
	}

	// If the user clicked on ignored or repaired buttons, need to remove the defect from the view
	updateDefectState(repair: AggregateModalData): void {
		if (repair.repairType !== "Pending") {
			this.selectionModel.clear();

			// we remove the defect from view with an API call so that the paginator still works appropriately
			this.updateTable();
		}
	}

	updateTable(resetPaginator: boolean = false): void {
		// reset paginator on filter change back to first page
		if (resetPaginator) {
			this.currentQueryStrings = {
				...this.currentQueryStrings,
				page: "1",
				perPage: this.pageSize.toString(),
			};
			this.pageIndex = 0;
			this.paginatorComponent?.resetPageIndex();
		}

		let queryStrings = { ...this.currentQueryStrings };

		if (this.companyId && this.userDivisions?.length > 0) {
			// Always include divisionId from user's profile to get legacy account code for GTC
			queryStrings = {
				...queryStrings,
				divisionId: this.userDivisions[0].id,
			};

			if (this.userHasAllDivisions !== undefined) {
				if (!queryStrings.divisionIds) {
					// if the user hasn't selected a division from the search bar, include the account selector divisionId in all defect requests
					if (!this.userHasAllDivisions) {
						queryStrings = {
							...queryStrings,
							divisionIds: this.userDivisions.map(division => division.id),
							allChildren: true,
						};
					}
				}
			}

			if (queryStrings.statuses) {
				this.store.dispatch(
					new GetDefectListWithQuery(
						this.companyId,
						queryStrings,
						environment.environmentConstants.APP_ENDPOINT_EVIR_V2,
					),
				);
			}
		}
	}

	buildDefectListViewModel(defects: Array<Defect>): OpenDefectTableViewModel[] {
		return defects.map((defect: Defect, index: number) => {
			return {
				assetId: defect.last.assetId,
				assetLocation: defect.last.assetDivision ? defect.last.assetDivision.divisionName : null,
				componentLabel: defect.last.componentName
					? this.languageDictionaryService.getTranslations(defect.last.componentName)
					: null,
				conditionLabel: defect.last.conditionName
					? this.languageDictionaryService.getTranslations(defect.last.conditionName)
					: null,
				configId: defect.last.configId,
				defectId: defect.defectId,
				lastInspectedDate: formatDate(newDate(defect.last.startTime), "P"),
				lastInspectedTime: formatDate(newDate(defect.last.startTime), "pp"),
				photos: defect.first.defectMedia
					? this.photoViewModelService.buildPhotoViewModel(defect.first.defectMedia)
					: null,
				reconciledAssetCategory: defect.reconciledAssetCategory,
				reconciledAssetId: defect.reconciledAssetId,
				reconciledAssetName: defect.reconciledAssetName,
				severity: defect.last.severity as number,
				zoneLabel: defect.last?.zoneName
					? this.languageDictionaryService.getTranslations(defect.last.zoneName)
					: null,
				inspectionType: defect.inspectionType,
				index: index,
			};
		});
	}

	public buildClosedDefectListViewModel(closedDefects: Defect[]): ClosedDefectTableViewModel[] {
		return closedDefects.map((defect, index) => {
			return {
				assetId: defect.last.assetId,
				assetLocation: defect.last.assetDivision ? defect.last.assetDivision.divisionName : null,
				componentLabel: defect.last.componentName
					? this.languageDictionaryService.getTranslations(defect.last.componentName)
					: null,
				conditionLabel: defect.last.conditionName
					? this.languageDictionaryService.getTranslations(defect.last.conditionName)
					: null,
				configId: defect.last.configId,
				reconciledAssetId: defect.reconciledAssetId,
				reconciledAssetName: defect.reconciledAssetName,
				repairedDate: defect?.repairs?.[0] ? formatDate(newDate(defect.repairs[0].created), "P") : "—",
				repairedTime: defect?.repairs?.[0] ? formatDate(newDate(defect.repairs[0].created), "pp") : "—",
				severity: defect.last.severity as number,
				zoneLabel: defect.last?.zoneName
					? this.languageDictionaryService.getTranslations(defect.last.zoneName)
					: null,
				index: index,
			};
		});
	}

	public handleImageClick(event: Event, defect: OpenDefectTableViewModel, i: number) {
		event.preventDefault();
		event.stopPropagation();
		this.openImageCarouselDialog(
			event.target as HTMLElement,
			defect.photos,
			i,
			translateAndFormat("defect photo", "title"),
		);
	}

	openImageCarouselDialog(trigger: HTMLElement, media: Array<DefectMedia>, i: number, title: string): void {
		this.dialog
			.open(ImageCarouselDialogComponent, {
				autoFocus: "#clear",
				restoreFocus: true,
				data: {
					title,
					media,
					imageIndex: i,
				},
			})
			.afterClosed()
			.subscribe(() => {
				if (trigger && typeof trigger.focus === "function") {
					trigger.focus();
				}
			});
	}

	currentlySelectedDefects() {
		const defects = this.selectionModel.selected;
		defects.sort((a, b) => a.index - b.index);

		return defects;
	}

	exportCSV() {
		this.exportService.exportDefectData(this.currentlySelectedDefects(), this.statusFilterValue, ExportType.CSV);
	}

	exportPDF() {
		this.exportService.exportDefectData(this.currentlySelectedDefects(), this.statusFilterValue, ExportType.PDF);
	}

	// Checks if all rows are selected
	isAllSelected(): boolean {
		const numSelected = this.selectionModel.selected.length;
		const numRows = this.getDataSource().data.length;
		return numSelected === numRows;
	}

	// Toggles between all and none selected
	masterToggle(): void {
		this.isAllSelected()
			? this.selectionModel.clear()
			: this.getDataSource().data.forEach(row => this.selectionModel.select(row));
	}

	isRejectedDefect(row: OpenDefectTableViewModel) {
		return row.inspectionType === InspectionType.rejected;
	}

	selectedHasRejectedDefect() {
		return this.selectionModel.selected.some(defect => this.isRejectedDefect(defect));
	}

	defectSelected() {
		return this.selectionModel.hasValue() && !this.selectedHasRejectedDefect();
	}

	ngOnDestroy() {
		this.defectSubscription?.unsubscribe();
		this.totalDefectSubscription?.unsubscribe();
	}

	private getDataSource() {
		return this.dataSource;
	}
}
